import InputSearch from '@components/ui/input-search'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@components/ui/sidebar'
import { useMutation } from '@connectrpc/connect-query'
import { useSession } from '@context/sessions'
import useDarkMode from '@hooks/use-dark-mode'
import {
  Cog,
  EyeOff,
  Home,
  LogIn,
  LogOut,
  Radio,
  ShieldCheck,
  SquarePlus,
  User,
} from 'lucide-react'
import React, { FC, MouseEvent } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { logout } from '@gen/api/v1/api-ApiService_connectquery'

const PageSidebar: FC = () => {
  const darkMode = useDarkMode()
  const navigate = useNavigate()
  const session = useSession()

  const logoutMutation = useMutation(logout)

  const exploreItems = [
    {
      title: 'For You',
      icon: Home,
      url: '/',
    },
    {
      title: 'Live',
      icon: Radio,
      url: '/live',
    },
    {
      title: 'Create',
      icon: SquarePlus,
      url: session?.user ? '/create' : '/login',
    },
  ]
  const settingsItems = [
    {
      title: 'Account Settings',
      icon: Cog,
      url: '/settings',
    },
    {
      title: 'Privacy Settings',
      icon: ShieldCheck,
      url: '/settings/privacy',
    },
    {
      title: 'Content Settings',
      icon: EyeOff,
      url: '/settings/content',
    },
  ]

  const handleLogout = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    try {
      await logoutMutation.mutateAsync({})
      toast.info('Logged out successfully')

      setTimeout(() => {
        navigate('/login')
      }, 1000)
    } catch (error) {}
  }

  return (
    <Sidebar>
      <SidebarContent>
        <SidebarMenu className="pt-4 px-2">
          <SidebarMenuItem>
            <a href="/">
              {darkMode ? (
                <img
                  className="max-h-[30px]"
                  src="/images/ancho-logo-white.svg"
                />
              ) : (
                <img
                  className="max-h-[30px]"
                  src="/images/ancho-logo-black.svg"
                />
              )}
            </a>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarMenu className="mt-4 px-2">
          <SidebarMenuItem>
            <form>
              <InputSearch placeholder="Search" />
            </form>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarGroup title="Explore">
          <SidebarGroupLabel>Explore</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {exploreItems.map((item, index) => (
                <SidebarMenuItem key={index}>
                  <SidebarMenuButton
                    asChild
                    isActive={item.url === location.pathname}
                  >
                    <a href={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {session?.user && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={'/profile' === location.pathname}
                  >
                    <a href="/profile">
                      <User />
                      <span>Profile</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        {session?.user ? (
          <SidebarGroup title="Settings">
            <SidebarGroupLabel>Settings</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {settingsItems.map((item, index) => (
                  <SidebarMenuItem key={index}>
                    <SidebarMenuButton
                      asChild
                      isActive={item.url === location.pathname}
                    >
                      <a href={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
                <SidebarMenuItem>
                  <SidebarMenuButton onClick={handleLogout}>
                    <LogOut />
                    <span>Logout</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <SidebarGroup title="Log in">
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild variant="outline">
                    <a href="/login">
                      <LogIn />
                      <span>Log in</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton asChild variant="outline">
                    <a href="/signup">
                      <User />
                      <span>Sign up</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        <div className="w-full text-center mt-4 text-xs text-muted-foreground">
          <a href="/">&copy; Copyright {new Date().getFullYear()} Ancho.</a>
        </div>
      </SidebarFooter>
    </Sidebar>
  )
}

export default PageSidebar
